/** Colonist creation + per-pawn stat helpers (skills, traits, mood inputs). */
import type { Colonist, Skills, SkillKey, NeedKey } from './types';
import { SKILL_KEYS } from './types';
import { TRAITS, traitById, FIRST_NAMES, STREET_NAMES, SKINS, ACCENTS, SIM } from './config';
import { Rng } from './rng';

function emptySkills(): Skills {
  const s = {} as Skills;
  for (const k of SKILL_KEYS) s[k] = { level: 0, xp: 0 };
  return s;
}

export function makeColonist(rng: Rng, id: number): Colonist {
  const first = rng.pick(FIRST_NAMES);
  const name = rng.next() < 0.5 ? `${first} "${rng.pick(STREET_NAMES)}"` : first;

  // 1–2 distinct traits
  const traits: string[] = [];
  const nTraits = rng.next() < 0.45 ? 2 : 1;
  while (traits.length < nTraits) {
    const t = rng.pick(TRAITS).id;
    if (!traits.includes(t)) traits.push(t);
  }

  // base skills + trait bonuses
  const skills = emptySkills();
  for (const k of SKILL_KEYS) skills[k].level = rng.int(6);
  for (const tid of traits) {
    const trait = traitById(tid);
    if (trait?.skill) {
      for (const k in trait.skill) {
        const key = k as SkillKey;
        skills[key].level = Math.min(SIM.SKILL_MAX, skills[key].level + (trait.skill[key] ?? 0));
      }
    }
  }

  return {
    id,
    name,
    skin: rng.pick(SKINS),
    accent: rng.pick(ACCENTS),
    traits,
    skills,
    needs: { rest: rng.range(70, 95), food: rng.range(70, 95), joy: rng.range(60, 90) },
    mood: 60,
    assignedRoomId: null,
    homeBunkId: null,
    activity: 'idle',
    floor: 0,
    x: 0.12,
    targetRoomId: null,
    targetFloor: 0,
    targetX: 0.12,
    ride: 0,
    downUntil: 0,
    breakUntil: 0,
    arrestedReturn: 0,
    recentThoughts: [],
  };
}

/** The colonist's best skill — used for the sprite's role look + roster label. */
export function topSkill(c: Colonist): SkillKey {
  let best: SkillKey = SKILL_KEYS[0];
  for (const k of SKILL_KEYS) if (c.skills[k].level > c.skills[best].level) best = k;
  return best;
}

export function workMul(c: Colonist): number {
  let m = 1;
  for (const tid of c.traits) m *= traitById(tid)?.workMul ?? 1;
  return m;
}

export function drainMul(c: Colonist, need: NeedKey): number {
  let m = 1;
  for (const tid of c.traits) m *= traitById(tid)?.drainMul?.[need] ?? 1;
  return m;
}

export function traitMoodFlat(c: Colonist): number {
  let f = 0;
  for (const tid of c.traits) f += traitById(tid)?.moodFlat ?? 0;
  return f;
}

export function isNightWorker(c: Colonist): boolean {
  return c.traits.some((tid) => traitById(tid)?.nightWorker);
}

export function defenseMul(c: Colonist): number {
  let m = 1;
  for (const tid of c.traits) m *= traitById(tid)?.defenseMul ?? 1;
  return m;
}

/** Available (not downed/arrested/broken) and present in the tower. */
export function isAvailable(c: Colonist, t: number): boolean {
  return c.downUntil <= t && c.arrestedReturn <= t && c.activity !== 'arrested';
}

/** Record a notable event on a colonist's short recent-events strip (see
 *  ColonistInspector). Display-only — does not feed back into `mood`. */
export function addThought(c: Colonist, now: number, id: string, label: string, mood: number, ttlSec = 30): void {
  c.recentThoughts = c.recentThoughts.filter((t) => t.expires > now);
  c.recentThoughts.unshift({ id: `${id}-${Math.round(now * 1000)}`, label, mood, expires: now + ttlSec });
  if (c.recentThoughts.length > 5) c.recentThoughts.length = 5;
}
